/**
 * Enhanced Asset Management Utility
 * 支持全路径资源加载，使用 @/ 别名或约定命名
 */

// 预加载所有资源（构建时）
const modules = import.meta.glob([
  '/src/assets/**/*',
  '/src/views/**/assets/**/*',
  '/src/components/**/assets/**/*'
], {
  eager: true,
  as: 'url'
})

/**
 * 获取资源 URL
 * @param {string} path - 资源路径，支持两种格式：
 *   - '@/assets/images/icons/logo.svg'（@/ 别名）
 *   - 'assets/images/icons/logo.svg'（约定命名）
 * @returns {string} 资源的 URL
 *
 * @example
 * // 全局资源
 * getAsset('@/assets/images/icons/logo.svg')
 * getAsset('assets/images/icons/logo.svg')
 *
 * // 页面级资源
 * getAsset('@/views/vip/assets/crown.svg')
 * getAsset('views/vip/assets/crown.svg')
 */
export function getAsset(path) {
  let resolvedPath = path

  if (path.startsWith('@/')) {
    resolvedPath = path.replace('@/', '/src/')
  } else if (!path.startsWith('/')) {
    resolvedPath = `/src/${path}`
  }

  if (modules[resolvedPath]) {
    return modules[resolvedPath]
  }

  handleAssetNotFound(path, resolvedPath)
  return path
}

/**
 * 处理资源未找到的情况
 */
function handleAssetNotFound(originalPath, resolvedPath) {
  const filename = originalPath.split('/').pop()
  const similarAssets = Object.keys(modules)
    .filter(key => key.toLowerCase().includes(filename.toLowerCase()))
    .slice(0, 5)

  console.error(`
❌ Asset not found: "${originalPath}"
   Resolved to: ${resolvedPath}
   ${similarAssets.length > 0 ? `Did you mean:\n${similarAssets.map(a => `     - ${a}`).join('\n')}` : 'No similar assets found.'}

   Supported formats:
   ✅ getAsset('@/assets/images/icons/logo.svg')
   ✅ getAsset('assets/images/icons/logo.svg')
   ✅ getAsset('@/views/vip/assets/crown.svg')
   ✅ getAsset('views/vip/assets/crown.svg')
  `.trim())
}

/**
 * 预加载图片资源
 * @param {string | string[]} urls - 图片 URL 或 URL 数组
 * @returns {Promise<string[]>} 返回加载完成的 URL 数组
 */
export function preloadImages(urls) {
  const urlArray = Array.isArray(urls) ? urls : [urls]

  const promises = urlArray.map(url => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(url)
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`))
      img.src = url
    })
  })

  return Promise.all(promises)
}
